import { useState, useEffect } from 'react';
import { playSuccessSound, playWrongSound, playDoneSound } from '../utils/audio';

interface WordObject {
  word: string;
  isFirst: boolean;
  id: string;
}

interface TypingModeHook {
  isTypingMode: boolean;
  slots: Array<{
    type: 'pre-filled' | 'empty' | 'user-filled';
    word: string | null;
    correctWord: string;
  }>;
  availableWords: Array<{
    id: number;
    word: string;
  }>;
  typingCompleted: boolean;
  showCompletedSentence: boolean;
  isIncorrect: boolean;
  handleWordSelect: (word: { id: number; word: string }) => void;
  handleRemoveWord: (index: number) => void;
  switchToTypingMode: () => void;
  resetTypingMode: () => void;
}

export function useTypingMode(
  sentence: string,
  onComplete: () => void
): TypingModeHook {
  const [isTypingMode, setIsTypingMode] = useState(false);
  const [slots, setSlots] = useState<Array<{
    type: 'pre-filled' | 'empty' | 'user-filled';
    word: string | null;
    correctWord: string;
  }>>([]);
  const [availableWords, setAvailableWords] = useState<Array<{
    id: number;
    word: string;
  }>>([]);
  const [typingCompleted, setTypingCompleted] = useState(false);
  const [showCompletedSentence, setShowCompletedSentence] = useState(false);
  const [isIncorrect, setIsIncorrect] = useState(false);

  useEffect(() => {
    if (isTypingMode && sentence) {
      initializeWords(sentence);
    }
  }, [isTypingMode, sentence]);

  const initializeWords = (text: string) => {
    // First split and clean the words
    const words = text.toLowerCase().replace(/[.,!?؟]/g, '').split(/\s+/).filter(Boolean);
    const N = words.length;
    
    // Store the first word to capitalize it later
    const firstWord = words[0];
    
    let preFilledIndices = [];
    if (N > 10) {
      const indices = Array.from({ length: N }, (_, i) => i);
      preFilledIndices = indices.sort(() => Math.random() - 0.5).slice(0, N - 10);
    }
    
    const newSlots = words.map((word, index) => {
      const isFirstWord = index === 0;
      return {
        type: preFilledIndices.includes(index) ? 'pre-filled' : 'empty',
        word: preFilledIndices.includes(index) ? 
          (isFirstWord ? word.charAt(0).toUpperCase() + word.slice(1) : word) : 
          null,
        correctWord: word,
      };
    });
    
    setSlots(newSlots);
    
    const available = words
      .filter((_, index) => !preFilledIndices.includes(index))
      .map((word, id) => ({ id, word }))
      .sort(() => Math.random() - 0.5)
      .map((word, index) => ({
        ...word,
        word: word.word === firstWord ? 
          word.word.charAt(0).toUpperCase() + word.word.slice(1) : 
          word.word
      }));
    
    setAvailableWords(available);
    setTypingCompleted(false);
    setShowCompletedSentence(false);
    setIsIncorrect(false);
  };

  const handleWordSelect = async (selectedWord: { id: number; word: string }) => {
    const firstEmptySlotIndex = slots.findIndex(slot => slot.type === 'empty');
    
    if (firstEmptySlotIndex >= 0) {
      const newSlots = [...slots];
      newSlots[firstEmptySlotIndex] = {
        ...newSlots[firstEmptySlotIndex],
        type: 'user-filled',
        word: selectedWord.word,
      };
      setSlots(newSlots);

      setAvailableWords(availableWords.filter(w => w.id !== selectedWord.id));

      const nextEmpty = newSlots.findIndex(slot => slot.type === 'empty');

      if (nextEmpty === -1) {
        const allCorrect = newSlots.every(slot => 
          slot.type === 'pre-filled' || 
          slot.word?.toLowerCase() === slot.correctWord.toLowerCase()
        );
        
        if (allCorrect) {
          setShowCompletedSentence(true);
          setTypingCompleted(true);
          onComplete();
        } else {
          setIsIncorrect(true);
          await playWrongSound();
          setTimeout(() => {
            resetTypingMode();
            setIsIncorrect(false);
            initializeWords(sentence);
          }, 1000);
        }
      }
    }
  };

  const handleRemoveWord = (index: number) => {
    if (slots[index].type !== 'user-filled') return;
    
    const removedWord = slots[index].word;
    setSlots(prev => {
      const newSlots = [...prev];
      newSlots[index] = { ...newSlots[index], type: 'empty', word: null };
      return newSlots;
    });
    
    setAvailableWords(prev => {
      const newId = prev.length > 0 ? Math.max(...prev.map(w => w.id)) + 1 : 0;
      return [...prev, { id: newId, word: removedWord! }];
    });
    
    setTypingCompleted(false);
    setShowCompletedSentence(false);
  };

  const switchToTypingMode = () => {
    setIsTypingMode(true);
  };

  const resetTypingMode = () => {
    setSlots([]);
    setAvailableWords([]);
    setTypingCompleted(false);
    setShowCompletedSentence(false);
    setIsIncorrect(false);
    setIsTypingMode(false);
  };

  return {
    isTypingMode,
    slots,
    availableWords,
    typingCompleted,
    showCompletedSentence,
    isIncorrect,
    handleWordSelect,
    handleRemoveWord,
    switchToTypingMode,
    resetTypingMode,
  };
}